import { useEffect, useRef, useState } from "react";
import posthog from "posthog-js";
import { Check, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { GROUNDY } from "@/components/onboarding/groundyArt";
import { OButton, OC, ScreenFrame, StepHeader, StepHeading, StepLine, TextLink } from "@/components/onboarding/onboardingUi";
import { SubmitCafeDialog } from "@/components/SubmitCafeDialog";
import { resolvePlaceName, type ResolvedPlace } from "@/lib/resolvePlaceName";
import { insertCityWaitlistRow, waitlistEventProps, type WaitlistPlace } from "@/lib/cityWaitlistVote";
import { getPendingVote, setPendingVote } from "@/lib/onboardingPending";
import type { HomeLocation } from "@/lib/homeLocation";

function capture(event: string, props: Record<string, unknown>) {
  try { posthog.capture(event, props); } catch { /* ignore */ }
}

/**
 * No cafés nearby: shown instead of Your picks when no café is within the
 * launch radius. The vote uses the same place lookup, insert and events as the
 * waitlist card, with source "onboarding". Its place details stay on the device
 * so the vote can be linked to the account once it exists.
 */
export function WaitlistStep({
  location,
  onChange,
  onContinue,
  onSkip,
  onJustLooking,
}: {
  location: HomeLocation & { lat: number; lng: number };
  onChange: () => void;
  onContinue: () => void;
  onSkip: () => void;
  onJustLooking: () => void;
}) {
  const { lat, lng } = location;
  const [resolved, setResolved] = useState<ResolvedPlace | null>(null);
  const [settled, setSettled] = useState(false);
  const [voted, setVoted] = useState(() => {
    const v = getPendingVote();
    return !!v && v.lat === lat && v.lng === lng;
  });
  const [submitting, setSubmitting] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSettled(false);
    resolvePlaceName(lat, lng)
      .then((p) => { if (!cancelled) { setResolved(p); setSettled(true); } })
      .catch(() => { if (!cancelled) setSettled(true); });
    return () => { cancelled = true; };
  }, [lat, lng]);

  const cityName = resolved?.city ?? location.city;
  const label = resolved?.label ?? [location.city, location.state].filter(Boolean).join(", ");
  const place: WaitlistPlace = {
    searched_city: label,
    city: resolved?.city ?? location.city ?? null,
    state: resolved?.state ?? location.state ?? null,
    lat,
    lng,
  };

  const shownRef = useRef<string | null>(null);
  useEffect(() => {
    if (!settled || shownRef.current === label) return;
    shownRef.current = label;
    capture("onboarding_empty_area", {
      searched_city: place.searched_city,
      city: place.city,
      state: place.state,
      lat: waitlistEventProps(place, "onboarding").lat,
      lng: waitlistEventProps(place, "onboarding").lng,
    });
    capture("city_waitlist_shown", { ...waitlistEventProps(place, "onboarding"), is_signed_in: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled, label]);

  const vote = async () => {
    if (!settled || submitting) return;
    setSubmitting(true);
    try {
      const error = await insertCityWaitlistRow({ place, email: null, userId: null, source: "onboarding" });
      if (error && error.code !== "23505") throw error;
      setPendingVote(place);
      setVoted(true);
      capture("city_waitlist_voted", { ...waitlistEventProps(place, "onboarding"), is_signed_in: false, email_provided: false });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ScreenFrame
      testId="waitlist-step"
      dock={
        <>
          {voted ? (
            <OButton onClick={onContinue}>Continue →</OButton>
          ) : (
            <OButton kind="outline" onClick={onSkip}>Skip for now</OButton>
          )}
          <TextLink onClick={onJustLooking}>Just looking? Explore cafés →</TextLink>
        </>
      }
    >
      <StepHeader groundy={GROUNDY.waitlist} eyebrow="Your area" />
      <StepHeading>You decide where Grounded goes next.</StepHeading>
      <StepLine>
        {`Grounded isn't in ${cityName} yet. Every vote counts toward the next city we score, and early voters earn the Founding Member badge when their city launches.`}
      </StepLine>
      <div className="mt-5 rounded-2xl p-3.5" style={{ backgroundColor: OC.white, border: "0.5px solid #E4DACA" }}>
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: OC.tile }}>
            <MapPin size={16} color={OC.cta} />
          </div>
          <span className="flex-1 min-w-0 truncate" style={{ fontSize: 15, fontWeight: 600, color: OC.text }}>{label}</span>
          <button onClick={onChange} className="underline" style={{ fontSize: 13, color: "#7B4F2E", fontWeight: 500 }}>
            Change
          </button>
        </div>
        <div className="mt-3">
          {voted ? (
            <div className="flex items-center gap-2 rounded-xl px-3 py-3" style={{ backgroundColor: "#EEF4EF", color: "#3F6B4C", fontSize: 14, fontWeight: 500 }} data-testid="vote-counted">
              <Check size={16} />
              <span>{`Your vote for ${cityName} is counted.`}</span>
            </div>
          ) : (
            <OButton onClick={vote} disabled={!settled || submitting} className="h-12">
              {submitting ? <Loader2 size={18} className="animate-spin" /> : `Add my vote for ${cityName}`}
            </OButton>
          )}
        </div>
      </div>
      <p className="mt-4" style={{ fontSize: 13, color: OC.textSec }}>
        Know a great work café there?{" "}
        <button onClick={() => setSuggestOpen(true)} className="underline" style={{ color: OC.text, fontWeight: 600 }}>
          Suggest a café
        </button>
      </p>
      <SubmitCafeDialog open={suggestOpen} onOpenChange={setSuggestOpen} entryPoint="onboarding_first_save" />
    </ScreenFrame>
  );
}
